import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import i18n from 'i18n';
import apiCubic from 'config/api-cubic';
import { USER_FLAGS } from 'config';
import requester from 'utils/requester';
import { universalLogin, loginUser } from 'actions/profile';
import { hideLoader, showLoader } from 'actions/loader';
import { AuthContext } from './AuthContext';
import Header from './common/Header';
import OTPForm from './common/OTPForm';
import ActionsSection from './ActionsSection';
import { StyledWhiteText } from './StyledComponents';
import useOnSuccessLogin from 'hooks/useOnSuccessLogin';
import { INIT_UI_LANGUAGE } from 'constants/languages';

const i18nScope = 'authorizationLoginOTPPage';
const SECTION_NAME = 'OTP_INPUT';
const RESEND_CODE_THRESHOLD_SECONDS = 59;
const MAX_OTP_ATTEMPS = 10;

const getDescription = ({ code, phone }) => {
  return [
    i18n.t(`${i18nScope}.description`),
    <br key="br" />,
    <StyledWhiteText key="login-value">{code + phone}</StyledWhiteText>,
  ];
};

const LoginOTPPage = () => {
  const dispatch = useDispatch();
  const { contextState } = useContext(AuthContext);
  const { code, phone, userFlag } = contextState;
  const [timeToNextResend, setTimeToNextResend] = useState(RESEND_CODE_THRESHOLD_SECONDS);
  const [attemps, setAttemps] = useState(0);

  const onSuccessLogin = useOnSuccessLogin();

  const updateTimeToNextResend = useCallback(
    () => setTimeToNextResend((value) => value && value - 1),
    [setTimeToNextResend],
  );

  const onSubmit = useCallback(
    (otp) => {
      const isLifecellUser = userFlag === USER_FLAGS.LIFECELL;
      const errorModal = { returnFocusTo: SECTION_NAME };
      let currentAttemptNumber = attemps + 1;

      // Using OTP as password fro lifecell
      // + Different phone format
      if (isLifecellUser) {
        dispatch(
          loginUser({
            login: `380${phone}`,
            password: otp,
            loginType: 'phone',
            userFlag,
            errorModal,
            onSuccess: onSuccessLogin,
          }),
        );
      } else {
        dispatch(
          universalLogin({
            phone,
            otp,
            userFlag,
            type: 'login',
            onSuccess: onSuccessLogin,
            errorModal,
          }),
        );
      }

      if (currentAttemptNumber >= MAX_OTP_ATTEMPS) {
        setTimeToNextResend(0);
        currentAttemptNumber = 0;
      }
      setAttemps(currentAttemptNumber);
    },
    [attemps, dispatch, onSuccessLogin, phone, userFlag],
  );

  const onResend = useCallback(async () => {
    try {
      const authToken = apiCubic.getAuthToken();

      dispatch(showLoader());

      await requester({
        method: 'post',
        url: `${apiCubic.host}${apiCubic.endpoints.universalAuthSendOTP}`,
        appendParams: false,
        extendHeadersWithSession: false,
        headers: { Authorization: `Basic ${authToken}` },
        data: {
          phone: `0${phone}`,
          language: INIT_UI_LANGUAGE,
        },
      });
    } finally {
      dispatch(hideLoader());
      setTimeToNextResend(RESEND_CODE_THRESHOLD_SECONDS);
    }
  }, [dispatch, phone]);

  useEffect(() => {
    const intervalId = setInterval(updateTimeToNextResend, 1000);
    return () => clearInterval(intervalId);
  });

  // STVWL-619, SMRT-351
  /*  useEffect(() => {
    if (!phone) history.goBack();
  }, [phone, history]);*/

  return (
    <>
      <Header title={i18n.t(`${i18nScope}.title`)} description={getDescription({ code, phone })} />
      <OTPForm
        sectionName={SECTION_NAME}
        buttonLabel={i18n.t(`${i18nScope}.next`)}
        invalidMessage={i18n.t(`${i18nScope}.otpInvalid`)}
        onSubmit={onSubmit}
      >
        {({ resetInput }) => {
          return (
            <ActionsSection.Button
              label={i18n.t(`${i18nScope}.${timeToNextResend <= 0 ? 'resendCode' : 'resendCodeTimer'}`, {
                seconds: `0${timeToNextResend}`.slice(-2),
              })}
              onEnter={() => {
                onResend();
                resetInput();
              }}
              disabled={timeToNextResend > 0}
            />
          );
        }}
      </OTPForm>
    </>
  );
};

export default LoginOTPPage;
